/**
 * KPI explanations — single source of truth for the small (i) tooltips
 * that appear next to financial metrics on the Analyze page.
 *
 * Tone: simple, intelligent, modern, non-academic. Goal: users learn
 * investing concepts gradually while using the platform.
 */
export const KPI_EXPLANATIONS = {
  market_cap: {
    title: "Market capitalization",
    what: "The total value of all the company's shares (price × number of shares).",
    why: "Tells you how big the business is. Big companies are usually more stable; smaller ones can grow faster but move more.",
    good: "Bigger isn't always better — what matters is value vs. fundamentals.",
  },
  pe: {
    title: "Price / Earnings (P/E)",
    what: "How much investors pay for $1 of the company's profit, using last year's earnings.",
    why: "A quick read on how expensive the stock is relative to what the company actually earns.",
    good: "Roughly < 15 cheap · 15–25 average · > 25 expensive (sector matters).",
  },
  forward_pe: {
    title: "Forward P/E",
    what: "Same as P/E, but using next year's expected earnings instead of last year's.",
    why: "A forward-looking valuation read — useful when growth is changing fast.",
    good: "If forward P/E is much lower than P/E, profits are expected to rise.",
  },
  eps: {
    title: "Earnings per share (EPS)",
    what: "Total profit divided by the number of shares outstanding.",
    why: "Lets you compare profit on a per-share basis across companies of different sizes.",
    good: "Higher and consistently growing EPS is generally a good sign.",
  },
  revenue: {
    title: "Revenue (TTM)",
    what: "Total sales the company generated in the trailing twelve months.",
    why: "Top-line sales. A company can't be profitable long-term without growing or stable revenue.",
    good: "Look for steady or growing revenue across multiple years.",
  },
  revenue_growth: {
    title: "Revenue growth",
    what: "How fast sales are growing year-over-year.",
    why: "Growing demand for the product is the foundation of long-term returns.",
    good: "> 10% strong · 5–10% solid · < 0% concerning.",
  },
  earnings_growth: {
    title: "Earnings growth",
    what: "How fast profits are growing year-over-year.",
    why: "Earnings growth eventually drives the share price.",
    good: "Sustained > 10% is excellent for mature businesses.",
  },
  profit_margin: {
    title: "Profit margin",
    what: "How much of every $1 of sales becomes profit, after all costs.",
    why: "A direct read on business quality and pricing power.",
    good: "> 20% premium business · 10–20% solid · < 5% thin.",
  },
  gross_margin: {
    title: "Gross margin",
    what: "What % of revenue is left after only the direct cost of producing the product or service.",
    why: "Shows the structural profitability of the product itself, before overhead.",
    good: "> 50% premium economics · 25–50% normal · < 20% commoditized.",
  },
  dividend_yield: {
    title: "Dividend yield",
    what: "Annual cash dividends per share divided by the share price.",
    why: "The cash return investors collect just for holding the stock.",
    good: "0–2% growth-oriented · 2–5% balanced · > 5% income (verify it's sustainable).",
  },
  roic: {
    title: "Return on invested capital (ROIC)",
    what: "How efficiently the company turns invested capital (debt + equity) into profit.",
    why: "The single best metric for business quality. High ROIC = the company creates real value.",
    good: "> 15% excellent · 10–15% solid · < 7% poor capital efficiency.",
  },
  fcf_quality: {
    // Phase 8.LP.HEAL2 — score-band copy (not ratio) because the
    // visible KPI tile displays this metric as a 0–100 score
    // (e.g. "77 / 100"). The prior "FCF / Net income near 1.0"
    // wording was correct for a ratio but nonsensical against a
    // score value — locked in by
    // frontend/src/lib/__tests__/kpi_explanations_fcf.test.js.
    title: "FCF Quality",
    what: "Shows how well reported earnings turn into real free cash flow.",
    why: "Strong cash-flow quality means profits are backed by actual cash generation, not just accounting earnings.",
    good: "80\u2013100 = strong \u00B7 60\u201379 = solid \u00B7 40\u201359 = mixed \u00B7 Below 40 = weak or higher risk",
  },
  sector_percentile: {
    title: "Sector percentile",
    what: "Where this stock's AI Opportunity Score ranks among peers in the same sector.",
    why: "Context — a 65 score is great in a weak sector and only OK in a strong one.",
    good: "Top 25% means the AI sees this as one of the better setups in its peer group.",
  },
  fcf_yield: {
    title: "Free cash flow yield",
    what: "Free cash flow per share divided by share price.",
    why: "How much real cash the business throws off relative to what you pay for the stock.",
    good: "> 6% attractive · 3–6% normal · < 2% expensive on a cash basis.",
  },
  debt_to_equity: {
    title: "Debt / Equity",
    what: "Total debt divided by shareholder equity — how much of the business is financed by lenders vs. owners.",
    why: "High leverage amplifies returns when things go well, but also amplifies risk when they don't.",
    good: "< 0.5 conservative · 0.5–1.5 normal (sector dependent) · > 2 aggressive.",
  },
  // ---- Phase 3 institutional concepts ----
  ev_ebitda: {
    title: "EV / EBITDA",
    what: "Enterprise value divided by EBITDA — a capital-structure-neutral valuation multiple.",
    why: "Useful for cyclical and asset-heavy companies because it strips out the effects of debt and accounting choices.",
    good: "Sector matters. As a rough guide: < 8x cheap · 8–14x normal · > 18x premium.",
  },
  pe_vs_sector: {
    title: "P/E vs sector",
    what: "Price-to-earnings relative to the sector median.",
    why: "Premium multiples must be justified by superior growth, margins, or capital efficiency.",
    good: "Within ±10% of sector is typical. Persistent premium needs a clear quality story.",
  },
  valuation_confidence: {
    title: "Valuation confidence",
    what: "How tightly different valuation methods agree on a fair value.",
    why: "If methods disagree widely, the central estimate is less reliable — even if it looks attractive.",
    good: "High: methods cluster within ~15%. Low: wide disagreement; treat the fair value with caution.",
  },
  multiple_compression: {
    title: "Multiple compression",
    what: "When investors are willing to pay a smaller multiple for the same earnings, so the stock can fall even if results don't change.",
    why: "Usually triggered by slowing growth, rising interest rates, or a re-rating of risk.",
    good: "Watch P/E and EV/EBITDA versus sector medians. A premium that can't be defended is a key downside trigger.",
  },
  value_trap: {
    title: "Value trap",
    what: "A stock that looks cheap on paper but whose fundamentals (margins, ROIC, FCF) are deteriorating.",
    why: "The valuation discount often reflects real weakness rather than an opportunity that will close.",
    good: "Cheap + improving fundamentals is a value setup. Cheap + worsening fundamentals is a trap.",
  },
  dilution: {
    title: "Dilution",
    what: "When a company issues new shares, each existing share owns a smaller slice of the business.",
    why: "Heavy stock-based compensation or repeated capital raises erode per-share value over time.",
    good: "Shares outstanding flat or shrinking is shareholder-friendly. Persistent share growth is a red flag.",
  },
};

// Phase 7.UI.10 — German tooltip translations for the most-visible KPI
// metrics. If a key isn't present here, getKpiInfo falls back to the
// English copy. Keeps the file additive — no risk to existing English.
const KPI_EXPLANATIONS_DE = {
  market_cap: {
    title: "Marktkapitalisierung",
    what: "Der Gesamtwert aller Aktien des Unternehmens (Kurs \u00D7 Aktienanzahl).",
    why: "Zeigt die Gr\u00F6\u00DFe des Unternehmens. Gro\u00DFe Firmen sind meist stabiler, kleinere k\u00F6nnen schneller wachsen, schwanken aber st\u00E4rker.",
    good: "Gr\u00F6\u00DFe ist nicht alles \u2014 entscheidend ist Bewertung vs. Fundamentaldaten.",
  },
  pe: {
    title: "Kurs-Gewinn-Verh\u00E4ltnis (KGV)",
    what: "Wie viel Anleger f\u00FCr 1 \u20AC Gewinn zahlen, basierend auf dem letzten Jahr.",
    why: "Schneller Indikator daf\u00FCr, wie teuer die Aktie im Verh\u00E4ltnis zum tats\u00E4chlichen Gewinn ist.",
    good: "Grob: < 15 g\u00FCnstig \u00B7 15\u201325 durchschnittlich \u00B7 > 25 teuer (Sektor beachten).",
  },
  forward_pe: {
    title: "Forward-KGV",
    what: "Wie das KGV, aber basierend auf dem erwarteten Gewinn des kommenden Jahres.",
    why: "Eine zukunftsgerichtete Bewertungskennzahl \u2014 hilfreich wenn das Wachstum sich \u00E4ndert.",
    good: "Ist das Forward-KGV deutlich niedriger als das KGV, werden steigende Gewinne erwartet.",
  },
  eps: {
    title: "Gewinn je Aktie (EPS)",
    what: "Gesamtgewinn geteilt durch die Anzahl ausstehender Aktien.",
    why: "Erm\u00F6glicht den Gewinnvergleich pro Aktie zwischen Unternehmen unterschiedlicher Gr\u00F6\u00DFe.",
    good: "H\u00F6heres und kontinuierlich wachsendes EPS ist meist ein gutes Zeichen.",
  },
  revenue: {
    title: "Umsatz (TTM)",
    what: "Gesamter Umsatz der letzten zw\u00F6lf Monate.",
    why: "Die Umsatzbasis. Ohne stabilen oder wachsenden Umsatz ist langfristige Profitabilit\u00E4t schwer.",
    good: "Achte auf stabilen oder wachsenden Umsatz \u00FCber mehrere Jahre.",
  },
  revenue_growth: {
    title: "Umsatzwachstum",
    what: "Wie schnell der Umsatz im Vergleich zum Vorjahr w\u00E4chst.",
    why: "Wachstum ist einer der st\u00E4rksten Treiber f\u00FCr langfristige Aktienrenditen.",
    good: "Konstantes zweistelliges Wachstum ist stark; Stagnation oder R\u00FCckgang ist ein Warnsignal.",
  },
  earnings_growth: {
    title: "Gewinnwachstum",
    what: "Wie schnell der Nettogewinn gegen\u00FCber dem Vorjahr w\u00E4chst.",
    why: "Gewinne treiben Aktienkurse \u2014 nicht Umsatz oder Buzz.",
    good: "Konstantes Gewinnwachstum signalisiert ein hochwertiges Unternehmen.",
  },
  profit_margin: {
    title: "Gewinnmarge",
    what: "Wie viel von 1 \u20AC Umsatz tats\u00E4chlich als Gewinn h\u00E4ngen bleibt.",
    why: "H\u00F6here Margen bedeuten Preissetzungsmacht und Effizienz.",
    good: "20%+ ist stark, 10\u201320% solide, unter 5% ist d\u00FCnn (sektorabh\u00E4ngig).",
  },
  dividend_yield: {
    title: "Dividendenrendite",
    what: "J\u00E4hrliche Dividende geteilt durch den Aktienkurs.",
    why: "Zeigt, was du als Eink\u00FCnfte erh\u00E4ltst, w\u00E4hrend du die Aktie h\u00E4ltst.",
    good: "Sehr hohe Renditen (>7%) k\u00F6nnen ein Warnsignal sein \u2014 vielleicht unsicher.",
  },
  roic: {
    title: "Kapitalrendite (ROIC)",
    what: "Wie viel Gewinn das Unternehmen pro investiertem Kapital erzeugt.",
    why: "Misst, wie gut Management das Geld der Investoren einsetzt.",
    good: "Konstant > 15% ist ein Qualit\u00E4tsmerkmal. < 8% ist schwach.",
  },
  fcf_quality: {
    // Phase 8.LP.HEAL2 — deutsche Score-Band-Formulierung.
    title: "FCF-Qualit\u00E4t",
    what: "Zeigt, wie gut ausgewiesene Gewinne in echten freien Cashflow umgewandelt werden.",
    why: "Starke Cashflow-Qualit\u00E4t bedeutet, dass Gewinne durch echte Cash-Generierung gest\u00FCtzt sind und nicht nur durch Buchhaltungseffekte.",
    good: "80\u2013100 = stark \u00B7 60\u201379 = solide \u00B7 40\u201359 = gemischt \u00B7 Unter 40 = schwach oder h\u00F6heres Risiko",
  },
  fcf_yield: {
    title: "Free-Cashflow-Rendite",
    what: "Free Cash Flow geteilt durch Marktkapitalisierung.",
    why: "Sagt, wie viel echtes Cash das Unternehmen pro investiertem Euro erzeugt.",
    good: "> 5% ist solide, > 8% sehr attraktiv (qualit\u00E4tsabh\u00E4ngig).",
  },
  debt_to_equity: {
    title: "Verschuldungsgrad",
    what: "Wie viel Fremdkapital das Unternehmen im Verh\u00E4ltnis zum Eigenkapital nutzt.",
    why: "Hoher Verschuldungsgrad erh\u00F6ht das Risiko, besonders bei steigenden Zinsen.",
    good: "Unter 1.0 ist konservativ, \u00FCber 2.0 ist aggressiv (branchenabh\u00E4ngig).",
  },
  sector_percentile: {
    title: "Sektor-Rang",
    what: "Zeigt, wie stark diese Aktie im Vergleich zu \u00E4hnlichen Aktien im selben Sektor abschneidet.",
    why: "Hilft, ein faires Vergleichsuniversum zu beurteilen \u2014 wer schl\u00E4gt die Peers, wer hinkt hinterher?",
    good: "Top 24% bedeutet: besser als ca. 76% der Vergleichswerte. Top 25% deutet auf Outperformance gegen\u00FCber Peers hin.",
  },
  gross_margin: {
    title: "Bruttomarge",
    what: "Anteil des Umsatzes, der nach den direkten Produktionskosten \u00FCbrig bleibt.",
    why: "Zeigt die strukturelle Profitabilit\u00E4t des Produkts vor Overhead.",
    good: "> 50% Premium-\u00D6konomie \u00B7 25\u201350% normal \u00B7 < 20% Commodity.",
  },
  pe_vs_sector: {
    title: "KGV vs. Sektor",
    what: "Kurs-Gewinn-Verh\u00E4ltnis im Vergleich zum Sektor-Median.",
    why: "Aufpreis-Multiples m\u00FCssen durch \u00FCberlegenes Wachstum, Margen oder Kapitaleffizienz gerechtfertigt sein.",
    good: "Innerhalb \u00B110% des Sektors ist \u00FCblich. Dauerhafter Aufpreis braucht eine klare Qualit\u00E4tsstory.",
  },
  ev_ebitda: {
    title: "EV / EBITDA",
    what: "Enterprise Value geteilt durch EBITDA \u2014 ein kapitalstruktur-neutrales Bewertungsmultiple.",
    why: "N\u00FCtzlich f\u00FCr zyklische und kapitalintensive Unternehmen, da Effekte aus Verschuldung und Bilanzierung neutralisiert werden.",
    good: "Sektor entscheidet. Grobe Richtwerte: < 8x g\u00FCnstig \u00B7 8\u201314x normal \u00B7 > 18x Aufpreis.",
  },
  valuation_confidence: {
    title: "Bewertungskonfidenz",
    what: "Wie eng verschiedene Bewertungsmethoden bei einem fairen Wert \u00FCbereinstimmen.",
    why: "Bei gro\u00DFer Streuung ist die zentrale Sch\u00E4tzung weniger verl\u00E4sslich \u2014 selbst wenn sie attraktiv aussieht.",
    good: "Hoch: Methoden liegen innerhalb von ~15%. Niedrig: gro\u00DFe Streuung; fairen Wert vorsichtig behandeln.",
  },
  multiple_compression: {
    title: "Multiple-Kompression",
    what: "Wenn Anleger bereit sind, ein niedrigeres Multiple f\u00FCr denselben Gewinn zu zahlen, kann der Kurs fallen, ohne dass sich die Ergebnisse \u00E4ndern.",
    why: "Typischerweise ausgel\u00F6st durch sich abschw\u00E4chendes Wachstum, steigende Zinsen oder eine Risiko-Neubewertung.",
    good: "KGV und EV/EBITDA vs. Sektor-Mediane beobachten. Ein nicht verteidigbarer Aufpreis ist ein wichtiger Abw\u00E4rtsausl\u00F6ser.",
  },
  value_trap: {
    title: "Value Trap",
    what: "Eine Aktie, die auf dem Papier g\u00FCnstig aussieht, deren Fundamentaldaten (Margen, ROIC, FCF) sich aber verschlechtern.",
    why: "Der Bewertungsabschlag spiegelt oft echte Schw\u00E4che wider, nicht eine Chance, die sich schlie\u00DFt.",
    good: "G\u00FCnstig + sich verbessernde Fundamentaldaten ist ein Value-Setup. G\u00FCnstig + sich verschlechternde Fundamentaldaten ist eine Falle.",
  },
  dilution: {
    title: "Verw\u00E4sserung",
    what: "Wenn ein Unternehmen neue Aktien ausgibt, geh\u00F6rt jeder bestehenden Aktie ein kleinerer Anteil des Gesch\u00E4fts.",
    why: "Hohe aktienbasierte Verg\u00FCtung oder wiederholte Kapitalerh\u00F6hungen erodieren mit der Zeit den Wert pro Aktie.",
    good: "Konstante oder sinkende Aktienanzahl ist aktion\u00E4rsfreundlich. Anhaltendes Aktienwachstum ist ein Warnzeichen.",
  },
};

export function getKpiInfo(key, locale) {
  // Phase 7.UI.10 — when locale === "de", prefer the German copy and
  // fall back to English if a key isn't translated yet. English-only
  // is unchanged.
  if (locale === "de" && KPI_EXPLANATIONS_DE[key]) {
    return KPI_EXPLANATIONS_DE[key];
  }
  return KPI_EXPLANATIONS[key] || null;
}
